import { createTheme, type Theme } from '@mui/material/styles';
import { TYPEFACES, type StromaMode, type StromaTokens } from './tokens';

/**
 * The simplified app's look (#1502, epic #1500): the mock "Stroma, first 24 hours"
 * as a token set and an MUI theme, the app's theme (`AppThemeProvider`).
 * Today's palettes, typefaces and theme (`tokens.ts`, `theme.ts`) are untouched.
 *
 * The values are the mock's `:root` tokens. Today's token names carry them, so a
 * component that reads `var(--ground)` or `var(--side1)` gets the mock's colour;
 * the mock's own names (`--hover`, `--sh-2`, …) are installed beside them for the
 * simplified surfaces (`SIMPLE_EXTRA_NAMES`).
 */

export type SimpleAccent = 'teal' | 'tangerine' | 'magenta' | 'violet';

/** The mock's four accents (Settings › Appearance, #1513); teal is the default. */
export const SIMPLE_ACCENTS: Record<SimpleAccent, string> = {
  teal: '#00A693',
  tangerine: '#B4582A',
  magenta: '#A54468',
  violet: '#5E54B0',
};

export const SIMPLE_TYPEFACE = 'geist' as const;

const BASE: Record<StromaMode, Record<string, string>> = {
  light: {
    bg: '#FFFFFF', side: '#FAFAFB', panel: '#FFFFFF', sunk: '#F7F7F9', hover: '#F2F2F5', active: '#EBEBF0',
    line: '#E6E6EB', 'line-2': '#F0F0F4',
    ink: '#0B0B0D', 'ink-2': '#4A4A55', 'ink-3': '#868693', 'ink-4': '#BDBDC7',
    ok: '#2F7A62', 'ok-bg': '#EAF3EF', run: '#A86514', 'run-bg': '#F7EFE2', bad: '#B4412F', 'bad-bg': '#F8EAE7',
    'on-acc': '#FFFFFF', you: '#F1F1F4', info: '#1976D2',
    'sh-1': '0 1px 2px rgba(16,16,24,.05)',
    'sh-2': '0 1px 2px rgba(16,16,24,.04),0 8px 24px -10px rgba(16,16,24,.14)',
    'sh-3': '0 2px 6px rgba(16,16,24,.06),0 28px 56px -18px rgba(16,16,24,.25)',
  },
  dark: {
    bg: '#0D0D10', side: '#111114', panel: '#141418', sunk: '#18181C', hover: '#1F1F24', active: '#27272D',
    line: '#26262C', 'line-2': '#1E1E23',
    ink: '#F4F4F6', 'ink-2': '#B6B6C0', 'ink-3': '#868692', 'ink-4': '#55555F',
    ok: '#7CC2A4', 'ok-bg': 'rgba(124,194,164,.12)', run: '#D9A75A', 'run-bg': 'rgba(217,167,90,.12)', bad: '#E08B7B', 'bad-bg': 'rgba(224,139,123,.12)',
    'on-acc': '#FFFFFF', you: '#202026', info: '#5A9BE0',
    'sh-1': '0 1px 2px rgba(0,0,0,.4)',
    'sh-2': '0 1px 2px rgba(0,0,0,.4),0 10px 28px -10px rgba(0,0,0,.6)',
    'sh-3': '0 2px 6px rgba(0,0,0,.4),0 28px 56px -18px rgba(0,0,0,.8)',
  },
};

function channels(hex: string): number[] {
  return [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
}

/** `color-mix(in srgb, a weight%, b)` for two #RRGGBB colours, as the mock derives its accent shades. */
export function mixHex(a: string, weight: number, b: string): string {
  const left = channels(a);
  const right = channels(b);
  return `#${left.map((channel, index) => Math.round(channel * weight + right[index] * (1 - weight))
    .toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

function alpha(hex: string, opacity: number): string {
  return `rgba(${channels(hex).join(',')},${opacity})`;
}

/**
 * The mock's accent shades: ink darker in light, lighter in dark; tint and soft over the ground. Text is what a word in
 * the accent is written in: it passes 4.5:1 on the ground in both modes (#00A693 itself gives 3.1:1 on white).
 */
function accentShades(accent: string, mode: StromaMode) {
  const ground = BASE[mode].bg;
  return mode === 'light'
    ? { text: mixHex(accent, 0.75, '#000000'), ink: mixHex(accent, 0.82, '#000000'), tint: mixHex(accent, 0.12, '#FFFFFF'), soft: mixHex(accent, 0.06, '#FFFFFF') }
    : { text: mixHex(accent, 0.72, '#FFFFFF'), ink: mixHex(accent, 0.72, '#FFFFFF'), tint: mixHex(accent, 0.22, ground), soft: mixHex(accent, 0.12, ground) };
}

/** Today's token names, filled with the mock's values. */
export function getSimpleTokens(mode: StromaMode = 'light', accentName: SimpleAccent = 'teal'): StromaTokens {
  const b = BASE[mode];
  const accent = SIMPLE_ACCENTS[accentName];
  const shade = accentShades(accent, mode);
  return {
    ground: b.bg,
    surface: b.panel,
    surface2: b.panel,
    sunk: b.sunk,
    line: b.line,
    'line-soft': b['line-2'],
    ink: b.ink,
    'ink-2': b['ink-2'],
    'ink-3': b['ink-3'],
    accent,
    'accent-hi': mode === 'light' ? mixHex(accent, 0.88, '#FFFFFF') : mixHex(accent, 0.86, '#FFFFFF'),
    'accent-ink': shade.ink,
    'accent-tint': shade.tint,
    'accent-soft': shade.soft,
    'accent-glow': alpha(accent, 0.35),
    accent2: accent,
    agent: b.info,
    'agent-tint': mixHex(b.info, mode === 'light' ? 0.1 : 0.16, b.bg),
    'on-accent': b['on-acc'],
    'on-agent': '#FFFFFF',
    // the mock's sidebar is off-white with ink text, not today's dark rail
    side1: b.side,
    side2: b.side,
    'side-ink': b['ink-2'],
    'side-hi': b.ink,
    'side-hover': b.hover,
    glow: alpha(accent, 0.18),
    wash: shade.soft,
    // the mock is flat: no inset gloss
    hi: 'rgba(255,255,255,0)',
    'shadow-c': mode === 'light' ? 'rgba(16,16,24,.14)' : 'rgba(0,0,0,.6)',
    shadow: b['sh-2'],
    // evidence keeps its meaning: confirmed green, likely amber, unconfirmed grey, refused red
    ok: b.ok,
    'ok-tint': b['ok-bg'],
    warn: b.run,
    'warn-tint': b['run-bg'],
    dim: b['ink-3'],
    'dim-tint': b.sunk,
    bad: b.bad,
    'bad-tint': b['bad-bg'],
    'on-ok': mode === 'light' ? '#FFFFFF' : b.bg,
    'on-warn': mode === 'light' ? '#FFFFFF' : b.bg,
    'on-dim': mode === 'light' ? '#FFFFFF' : b.bg,
    'on-bad': mode === 'light' ? '#FFFFFF' : b.bg,
  };
}

/** The mock's own variable names, installed only while the variant is on. */
export const SIMPLE_EXTRA_NAMES = [
  '--side', '--panel', '--hover', '--active', '--line-2', '--ink-4', '--you',
  '--acc', '--acc-ink', '--acc-tint', '--acc-soft', '--acc-text', '--on-acc',
  '--run', '--run-bg', '--ok-bg', '--bad-bg',
  '--sh-1', '--sh-2', '--sh-3', '--ui',
] as const;

export function simpleExtraCssVariables(mode: StromaMode = 'light', accentName: SimpleAccent = 'teal'): Record<typeof SIMPLE_EXTRA_NAMES[number], string> {
  const b = BASE[mode];
  const accent = SIMPLE_ACCENTS[accentName];
  const shade = accentShades(accent, mode);
  return {
    '--side': b.side,
    '--panel': b.panel,
    '--hover': b.hover,
    '--active': b.active,
    '--line-2': b['line-2'],
    '--ink-4': b['ink-4'],
    '--you': b.you,
    '--acc': accent,
    '--acc-ink': shade.ink,
    '--acc-tint': shade.tint,
    '--acc-soft': shade.soft,
    '--acc-text': shade.text,
    '--on-acc': b['on-acc'],
    '--run': b.run,
    '--run-bg': b['run-bg'],
    '--ok-bg': b['ok-bg'],
    '--bad-bg': b['bad-bg'],
    '--sh-1': b['sh-1'],
    '--sh-2': b['sh-2'],
    '--sh-3': b['sh-3'],
    '--ui': TYPEFACES[SIMPLE_TYPEFACE].body,
  };
}

/** The mock's radii: controls 8, cards 12, menus 11, dialogs and the palette 14. */
export const SIMPLE_RADII = { control: 8, card: 12, menu: 11, dialog: 14, composer: 18 } as const;

export function createSimpleTheme(mode: StromaMode = 'light', accentName: SimpleAccent = 'teal'): Theme {
  const t = getSimpleTokens(mode, accentName);
  const b = BASE[mode];
  const fonts = TYPEFACES[SIMPLE_TYPEFACE];
  const heading = { fontFamily: fonts.body, fontWeight: 600 };
  const toast = { color: t.ground, background: t.ink, border: 0, boxShadow: b['sh-3'] };

  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent, light: t['accent-hi'], dark: t['accent-ink'], contrastText: t['on-accent'] },
      secondary: { main: t['ink-2'], contrastText: t.ground },
      success: { main: t.ok, contrastText: t['on-ok'] },
      warning: { main: t.warn, contrastText: t['on-warn'] },
      error: { main: t.bad, contrastText: t['on-bad'] },
      info: { main: t.agent, contrastText: t['on-agent'] },
      background: { default: t.ground, paper: t.surface },
      text: { primary: t.ink, secondary: t['ink-2'], disabled: t['ink-3'] },
      divider: t.line,
      action: { hover: b.hover, selected: b.active, focus: t['accent-soft'], disabled: t['ink-3'] },
    },
    shape: { borderRadius: SIMPLE_RADII.control },
    shadows: [
      'none', b['sh-1'], b['sh-1'], b['sh-2'], b['sh-2'], b['sh-2'], b['sh-2'], b['sh-2'],
      b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'],
      b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'], b['sh-3'],
      b['sh-3'],
    ],
    typography: {
      fontFamily: fonts.body,
      fontSize: 13.5,
      htmlFontSize: 16,
      h1: { ...heading, fontSize: 34, lineHeight: 1.2, letterSpacing: '-0.03em' },
      h2: { ...heading, fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.025em' },
      h3: { ...heading, fontSize: 24, lineHeight: 1.2, letterSpacing: '-0.01em' },
      h4: { ...heading, fontSize: 24, lineHeight: 1.2, letterSpacing: '-0.01em' },
      h5: { ...heading, fontSize: 18, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h6: { ...heading, fontSize: 14.5, lineHeight: 1.35 },
      subtitle1: { fontSize: 13.5, lineHeight: 1.4, fontWeight: 500 },
      subtitle2: { fontSize: 12.5, lineHeight: 1.4, fontWeight: 500 },
      body1: { fontSize: 13.5, lineHeight: 1.5 },
      body2: { fontSize: 13, lineHeight: 1.5 },
      button: { fontSize: 13, lineHeight: 1.2, fontWeight: 500, letterSpacing: 0, textTransform: 'none' },
      caption: { fontSize: 12, lineHeight: 1.45 },
      overline: { fontSize: 11.5, lineHeight: 1.35, fontWeight: 500, letterSpacing: 0, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': { colorScheme: mode },
          body: { backgroundColor: t.ground, color: t.ink, fontFamily: fonts.body, fontSize: 13.5, lineHeight: 1.5, WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', textRendering: 'optimizeLegibility' },
          'code, pre, kbd, samp, [data-number], .stroma-number': { fontFamily: fonts.mono, fontVariantNumeric: 'tabular-nums' },
          '::selection': { backgroundColor: t['accent-tint'], color: t.ink },
          ':focus-visible': { outlineColor: t.accent },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundColor: t.surface, backgroundImage: 'none', border: `1px solid ${t.line}` }, rounded: { borderRadius: SIMPLE_RADII.card } } },
      MuiCard: { styleOverrides: { root: { backgroundColor: t.surface, backgroundImage: 'none', border: `1px solid ${t.line}`, borderRadius: SIMPLE_RADII.card, boxShadow: b['sh-1'] } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 30, borderRadius: SIMPLE_RADII.control, padding: '5px 12px' },
          containedPrimary: { color: t.ground, background: t.ink, '&:hover': { background: t.ink, opacity: 0.88 } },
          outlined: { color: t.ink, borderColor: t.line, background: t.ground, boxShadow: b['sh-1'], '&:hover': { borderColor: t.line, background: b.hover } },
          text: { color: t['ink-2'], '&:hover': { color: t.ink, background: b.hover } },
          sizeSmall: { minHeight: 26, padding: '3px 9px', fontSize: 12.5 },
        },
        variants: [{ props: { variant: 'run' as const }, style: { color: t['on-ok'], background: t.ok, '&:hover': { background: t.ok, opacity: 0.88 } } }],
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 7, color: t['ink-2'], '&:hover': { color: t.ink, background: b.hover } } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: SIMPLE_RADII.control, fontWeight: 500, fontSize: 12.5 },
          outlined: { borderColor: t.line, color: t['ink-2'] },
          sizeSmall: { height: 22, fontSize: 11.5 },
        },
        variants: [
          { props: { className: 't-confirmed' }, style: { color: t.ok, background: t['ok-tint'], borderColor: t.ok } },
          { props: { className: 't-emerging' }, style: { color: t.warn, background: t['warn-tint'], borderColor: t.warn } },
          { props: { className: 't-unconfirmed' }, style: { color: t.dim, background: t['dim-tint'], borderColor: t.dim } },
          { props: { className: 't-weakened' }, style: { color: t.bad, background: t['bad-tint'], borderColor: t.bad } },
        ],
      },
      MuiTabs: { styleOverrides: { root: { minHeight: 40 }, indicator: { height: 2, background: t.ink } } },
      MuiTab: { styleOverrides: { root: { minHeight: 40, fontSize: 13, fontWeight: 400, textTransform: 'none', color: t['ink-3'], '&.Mui-selected': { color: t.ink, fontWeight: 500 } } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: SIMPLE_RADII.dialog, boxShadow: b['sh-3'] } } },
      MuiDialogTitle: { styleOverrides: { root: { fontFamily: fonts.body, fontWeight: 600, fontSize: 16 } } },
      MuiTableCell: { styleOverrides: { head: { color: t['ink-3'], background: t.sunk, fontSize: 12, fontWeight: 500, borderColor: t.line }, body: { fontSize: 13, borderColor: t['line-soft'] } } },
      MuiTableRow: { styleOverrides: { root: { '&:hover': { backgroundColor: t.sunk } } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: SIMPLE_RADII.control, background: t.ground, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: b['ink-4'] }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mixHex(t.accent, 0.4, t.line), borderWidth: 1 } }, notchedOutline: { borderColor: t.line } } },
      MuiInputLabel: { styleOverrides: { root: { color: t['ink-3'] } } },
      MuiTooltip: { styleOverrides: { tooltip: { ...toast, borderRadius: 7, fontSize: 11.5 }, arrow: { color: t.ink } } },
      MuiPopover: { styleOverrides: { paper: { borderRadius: SIMPLE_RADII.card, boxShadow: b['sh-3'] } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: SIMPLE_RADII.menu, boxShadow: b['sh-3'] } } },
      MuiMenuItem: { styleOverrides: { root: { fontSize: 13, borderRadius: 7, '&:hover': { background: b.hover } } } },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: t.surface, borderColor: t.line, boxShadow: b['sh-3'], '&.MuiDrawer-paperAnchorLeft, &.MuiDrawer-paperAnchorRight': { width: 460, maxWidth: '100vw' } } } },
      MuiSnackbarContent: { styleOverrides: { root: { ...toast, borderRadius: 10, fontSize: 13 } } },
      MuiLinearProgress: { styleOverrides: { root: { height: 3, borderRadius: 0, backgroundColor: t['line-soft'] }, bar: { borderRadius: 0, background: t.accent } } },
    },
  });
}
